import { env } from '../../config/env';
import { tokenStorage } from '../../api/tokenStorage';
import type { CheckoutPayload } from '../../types/api';
import type { AdminOrderRequest, Order, OrderRequest, OrderRequestStatus, OrderStatus } from '../../types';
import { parseMockToken } from '../mock/storage';
import * as http from './ordersService.http';
import * as mock from './ordersService.mock';

export interface OrdersService {
  fetchOrders(): Promise<Order[]>;
  createOrder(payload: CheckoutPayload): Promise<Order>;
  getOrder(orderId: string): Promise<Order | null>;
  fetchAllOrders(): Promise<Order[]>;
  updateOrderStatus(orderId: string, status: OrderStatus): Promise<Order>;
  updateOrderTracking(orderId: string, trackingNumber: string | null): Promise<Order>;
  uploadAdminOrderInvoice(orderId: string, file: File): Promise<Order>;
  downloadOrderInvoice(orderId: string): Promise<void>;
  downloadAdminOrderInvoice(orderId: string): Promise<void>;
  submitCancellationRequest(orderId: string, reason: string): Promise<OrderRequest>;
  withdrawOrderRequest(orderId: string, requestId: string): Promise<void>;
  adminCancelOrder(orderId: string, adminNote?: string | null): Promise<Order>;
  fetchAdminOrderRequests(status?: OrderRequestStatus): Promise<AdminOrderRequest[]>;
  updateAdminOrderRequest(
    requestId: string,
    status: 'approved' | 'rejected',
    adminNote?: string | null,
  ): Promise<AdminOrderRequest>;
}

function getMockSessionUserId(): string {
  const userId = parseMockToken(tokenStorage.get() ?? '');
  if (!userId) {
    throw new Error('Oturum gerekli.');
  }
  return userId;
}

const mockOrdersService: OrdersService = {
  fetchOrders: () => mock.mockFetchOrders(getMockSessionUserId()),
  createOrder: (payload) => mock.mockCreateOrder(getMockSessionUserId(), payload),
  getOrder: (orderId) => mock.mockGetOrder(getMockSessionUserId(), orderId),
  fetchAllOrders: mock.mockFetchAllOrders,
  updateOrderStatus: mock.mockUpdateOrderStatus,
  updateOrderTracking: mock.mockUpdateOrderTracking,
  uploadAdminOrderInvoice: mock.mockUploadAdminOrderInvoice,
  downloadOrderInvoice: mock.mockDownloadOrderInvoice,
  downloadAdminOrderInvoice: mock.mockDownloadAdminOrderInvoice,
  submitCancellationRequest: (orderId, reason) =>
    mock.mockSubmitCancellationRequest(getMockSessionUserId(), orderId, reason),
  withdrawOrderRequest: (orderId, requestId) =>
    mock.mockWithdrawOrderRequest(getMockSessionUserId(), orderId, requestId),
  adminCancelOrder: mock.mockAdminCancelOrder,
  fetchAdminOrderRequests: mock.mockFetchAdminOrderRequests,
  updateAdminOrderRequest: mock.mockUpdateAdminOrderRequest,
};

const httpOrdersService: OrdersService = {
  fetchOrders: http.httpFetchOrders,
  createOrder: http.httpCreateOrder,
  getOrder: (orderId) => http.httpGetOrder(orderId),
  fetchAllOrders: http.httpFetchAllOrders,
  updateOrderStatus: http.httpUpdateOrderStatus,
  updateOrderTracking: http.httpUpdateOrderTracking,
  uploadAdminOrderInvoice: http.httpUploadAdminOrderInvoice,
  downloadOrderInvoice: http.httpDownloadOrderInvoice,
  downloadAdminOrderInvoice: http.httpDownloadAdminOrderInvoice,
  submitCancellationRequest: http.httpSubmitCancellationRequest,
  withdrawOrderRequest: http.httpWithdrawOrderRequest,
  adminCancelOrder: http.httpAdminCancelOrder,
  fetchAdminOrderRequests: http.httpFetchAdminOrderRequests,
  updateAdminOrderRequest: http.httpUpdateAdminOrderRequest,
};

export const ordersService: OrdersService = env.useMock ? mockOrdersService : httpOrdersService;
